import React, { useMemo, useRef, useState } from 'react';

import mergeWith from 'lodash/mergeWith';
import noop from 'lodash/noop';

import Autocomplete from '@mui/material/Autocomplete';

import { useUpdateEffect } from '@src/corelib/hooks';

import { SyntheticEvent } from '../primitives';
import TextField from './TextField';

// Type email raise warning
// https://www.chromium.org/developers/design-documents/create-amazing-password-forms

const DOMAINS = [
  // 'gmail.com',
  // 'outlook.com',
  // 'hotmail.com',
  // 'icloud.com',
];

const styles = {
  paper: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
};

function createOptions(value) {
  if (!value.trim() || DOMAINS.length === 0) {
    return [];
  }

  const [namePart, domainPart] = value.split('@');

  // When no '@' is typed, suggest all domains
  if (!domainPart) {
    return DOMAINS.map((domain) => `${namePart}@${domain}`);
  }
  // When '@' is typed without a domain, still suggest all domains
  if (domainPart === '') {
    return DOMAINS.map((domain) => `${namePart}@${domain}`);
  }

  return DOMAINS.filter((domain) => domain.startsWith(domainPart)).map((domain) => `${namePart}@${domain}`);
}

const EmailField = React.forwardRef((props, ref) => {
  const {
    value = '',
    onChange = noop,
    InputProps = {},
    error = false,
    fullWidth = false,
    helperText = ' ',
    name,
    onBlur = noop,
    placeholder,
    ...restProps
  } = props;

  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(value);
  const options = useMemo(() => createOptions(inputValue), [inputValue]);

  useUpdateEffect(() => {
    const event = new SyntheticEvent('change', name, inputValue);
    onChange(event);
  }, [inputValue]);

  const handleInputChange = (event) => {
    switch (event.type) {
      case 'change':
        setInputValue(event.target.value);
        break;
      case 'click':
        setInputValue(event.target.textContent);
        break;
      default:
        console.warn(`The event with type=${event.type} is not supported.`);
    }
  };

  const renderInput = (params) => {
    const _InputProps = mergeWith({}, InputProps, params.InputProps, (objValue, srcValue) => {
      // Use srcValue if objValue is undefined, otherwise use objValue
      return objValue === undefined ? srcValue : objValue;
    });

    return (
      <TextField
        {...restProps}
        ref={ref}
        name={name}
        type="text"
        error={error}
        id={params.id}
        inputRef={inputRef}
        helperText={helperText}
        InputProps={_InputProps}
        placeholder={placeholder}
        disabled={params.disabled}
        fullWidth={fullWidth}
        autoComplete="email username"
        inputProps={params.inputProps}
        InputLabelProps={params.InputLabelProps}
        onBlur={onBlur}
      />
    );
  };

  return (
    <Autocomplete
      freeSolo
      openOnFocus
      disablePortal
      size="small"
      options={options}
      value={inputValue}
      fullWidth={fullWidth}
      disableClearable={!inputValue}
      slotProps={{
        paper: {
          sx: styles.paper,
        },
      }}
      onInputChange={handleInputChange}
      renderInput={renderInput}
    />);
});

EmailField.displayName = 'EmailField';
export default EmailField;
